import { MediaStreamRecorder, PitchCapture, useAudioEngine } from '@tunasong/audio-ui'
import { MelodyLib, VocalLib, type CaptureDiagnosis, type PitchFrame } from '@tunasong/music-lib'
import { useRef, useState } from 'react'

/**
 * Microphone capture for a melody take: the frames the tracker produced, and a recording of the same
 * performance.
 *
 * The recording comes off the SAME `MediaStream` the tracker opened. Two streams would be two
 * microphones as far as the browser is concerned - different constraints, different gain, a second
 * recording indicator - and the audio entity would then hold a performance that is not quite the one
 * the notes were derived from.
 *
 * There is no mute here, and that is deliberate. The Pitch Trainer mutes everything for its capture
 * window because a reference bleeding into the take would be scored as singing; here the click has
 * to sound for the whole take, which is why headphones are a precondition rather than a
 * recommendation (§5).
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §5
 */

/**
 * Frames to raw notes: the vocal pipeline, unchanged, plus the melody-specific mapping.
 *
 * Split out of the hook so the whole chain is testable from hand-written frames - which is the only
 * way any of it can be tested in a worktree with no microphone.
 */
export const framesToRawNotes = (
  frames: PitchFrame[],
  t0Ms: number,
  /**
   * The capture's sample rate, which is what turns a frame's `onsetSampleOffset` into an onset. Left
   * optional so a caller without a capture context keeps the hop-quantized onsets this chain had
   * before the worklet started reporting the foot of the attack.
   */
  sampleRate?: number
): { raw: MelodyLib.RawNote[]; diagnosis: CaptureDiagnosis } => {
  const diagnosis = VocalLib.diagnoseCapture(frames)
  const voiced = VocalLib.voicedFrames(frames)
  const segments = VocalLib.segmentNotes(voiced, { sampleRate })

  /*
   * The floor is not passed on: velocity is relative within the take now (Bowie's §8 ruling), so
   * the noise floor's only job is the voicing gate inside `voicedFrames`, one line above.
   */
  return { raw: MelodyLib.segmentsToRawNotes(segments, { frames, t0Ms }), diagnosis }
}

export interface AudioCapture {
  startFrames: (onFrame: (frame: PitchFrame) => void) => Promise<() => void>
  startRecording: () => Promise<() => Promise<Blob | null>>
  /** The microphone in use, as the browser names it. Drives the meter and the headphone consent. */
  deviceLabel: string | null
  /**
   * The rate the tracker is running at. Carried out of the hook because `segmentNotes` needs it to
   * read a frame's `onsetSampleOffset`: the worklet reports a note's onset in samples, and only the
   * capture knows what a sample is worth.
   */
  sampleRate: number
}

export const useAudioCapture = (): AudioCapture => {
  const engine = useAudioEngine()
  const [deviceLabel, setDeviceLabel] = useState<string | null>(null)
  /** A ref, not state: `startRecording` runs in the same tick and cannot wait for a re-render. */
  const capture = useRef<PitchCapture | null>(null)

  const startFrames = async (onFrame: (frame: PitchFrame) => void) => {
    /*
     * A fresh capture per take, released when the take ends: a long-lived one would hold the
     * microphone - and its recording indicator - open between takes, which a tool that promises the
     * take is the only thing recorded should not do.
     */
    const pitch = new PitchCapture({ context: engine.mixer.context })
    const listener = (ev: CustomEvent<PitchFrame>) => onFrame(ev.detail)
    pitch.addFrameListener(listener)
    await pitch.start()
    capture.current = pitch
    setDeviceLabel(pitch.deviceLabel)

    return () => {
      pitch.removeFrameListener(listener)
      pitch.stop()
      capture.current = null
    }
  }

  const startRecording = async () => {
    const stream = capture.current?.stream
    if (!stream) {
      /** No stream means `startFrames` has not run: a take with no recording, not a crash. */
      return async () => null
    }
    const recorder = new MediaStreamRecorder(stream, { type: 'audio' })
    await recorder.start()

    return async () => {
      /*
       * `stop()` then `getRecording()` in the same tick, as `AudioRecorder` already does it. The
       * recorder collects in 10 ms chunks, so at most the final chunk is still in flight - and
       * inventing a different handshake here would make the melody's recording path differ from the
       * one the rest of the app has shipped.
       */
      recorder.stop()
      const blob = recorder.getRecording()
      recorder.destroy()
      return blob
    }
  }

  return { startFrames, startRecording, deviceLabel, sampleRate: engine.mixer.context.sampleRate }
}
